/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What the `Shared…` chip does, decided once for every value that has one** — `X30c` (`#1303`),
 * reported by Todd 2026-09-10: *"shared expressions within another expression don't have the
 * open/edit/replace capability. For example, a shared expression in a conditional or expressions."*
 *
 * `X30a` gave a **row** three acts — the chip opens what it holds, a control inside replaces it, the
 * `✕` comes off it — and built them in `MappingControl`. A branch outcome and a calculation operand
 * each had their own copy of the older behaviour, where the set chip opened the **picker**, so the
 * capability existed at the top level of a value and nowhere inside it.
 *
 * > **Is this a property of *this surface*, or of *a value that addresses a shared expression*?**
 *
 * The second, and it always was: three components held the same two `useState`s and the same
 * *toggle the picker* handler, which is why fixing one fixed one. This is that state machine, named,
 * and the three now differ only in **where** they place the two surfaces it opens — which is
 * genuinely per-surface, since a row has zones and a branch has a coloured box.
 *
 * It answers `openSlot` too, because *which chip is lit* is the same fact as *what is open below*
 * and was already drifting when they were separate (`#1229`: clicking `Shared…` lit nothing).
 *
 * **The caller holds it rather than the value line**, for one reason: while the picker is open it is
 * *the only thing* under the chip row, because an editor for a value about to be replaced is two
 * live surfaces for one decision (`#1229`). Only the caller can suppress the form it renders below,
 * so only the caller can be the one asking.
 */

import React from 'react';
import type { MacroExpression, ReferenceExpression } from '../../../types/decorator-expression';
import type { CapabilitySlot } from './CapabilityChipRow';

export interface SharedExpressionSlot {
  /** The address this value holds, or `undefined` where it holds a value of its own. */
  reference: ReferenceExpression | undefined;
  /** Show the expression this value addresses, read-only, with the way to edit it beside it. */
  panelOpen: boolean;
  /** Choose an expression from the library — for a value that has none, or is being pointed elsewhere. */
  pickerOpen: boolean;
  /** What the chip row should light. `null` where neither surface is open. */
  openSlot: CapabilitySlot | null;
  /** The chip was clicked: open what it holds, or offer the library where it holds nothing. */
  activate: () => void;
  closePanel: () => void;
  closePicker: () => void;
  /** From inside the panel: point this value at a different expression. */
  pickAnother: () => void;
}

export function useSharedExpressionSlot(expression: MacroExpression | undefined): SharedExpressionSlot {
  const reference = expression?.type === 'reference' ? expression : undefined;
  const [panelRequested, setPanelRequested] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);

  /**
   * **Derived from the value, never only remembered** — the rule `CapabilityChipRow` pays for one
   * level down. The flag outlives the value ceasing to be a reference (the `✕`, a pick, an errand),
   * and a panel left open over a value with no address would be looking one up that is not there.
   */
  const panelOpen = panelRequested && !pickerOpen && !!reference;
  React.useEffect(() => {
    if (panelRequested && !panelOpen) setPanelRequested(false);
  }, [panelRequested, panelOpen]);

  return {
    reference,
    panelOpen,
    pickerOpen,
    openSlot: panelOpen || pickerOpen ? 'shared' : null,
    /**
     * **A set chip opens what it holds; an unset one offers the library** (`X30a`, pattern 32). One
     * gesture meaning two things on one chip is what made *show me this* mean *replace this* on the
     * only shape a row does not own.
     */
    activate: () => {
      if (reference) setPanelRequested((prev) => !prev);
      else setPickerOpen((prev) => !prev);
    },
    closePanel: () => setPanelRequested(false),
    closePicker: () => setPickerOpen(false),
    /* Closed rather than stacked: the picker replaces this value, so leaving the panel open under it
       would be two live surfaces for one decision (`#1229`). */
    pickAnother: () => {
      setPanelRequested(false);
      setPickerOpen(true);
    },
  };
}
